<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import Accordion from '@/js/components/Accordion.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import TextInput from '@/js/components/TextInput.vue';
    import TextareaInput from '@/js/components/TextareaInput.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import CheckboxInput from '@/js/components/CheckboxInput.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetAsrConfigRules,
        useQueryGetAsrConfigFilterOptions,
        useQueryGetAsrConfigExternalSystems,
        useMutationCreateAsrConfigRule,
        useMutationUpdateAsrConfigRule,
        useMutationDeleteAsrConfigRule,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import type { AsrConfigRule, AsrChannelType } from '@/js/services/api/backOffice/asrConfigApi';

    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();

    const { data: tenantsData } = useQueryGetBackOfficeTenants();

    type Mode = 'list' | 'create' | 'edit';
    type MatchType = 'global' | 'queue' | 'skill';
    type ChannelMode = 'all' | 'type' | 'specific';

    // Keep in sync with ASR-Prompt-Service/services/Prompt_messages/prompt_utils.py LANGUAGES -
    // an unsupported code raises a KeyError there, so this is a hard gate, not just a UI nicety.
    const OVERVIEW_LANGUAGE_OPTIONS: Option[] = [
        { value: 'sq', name: 'Albanian' },
        { value: 'bg', name: 'Bulgarian' },
        { value: 'hr', name: 'Croatian' },
        { value: 'cs', name: 'Czech' },
        { value: 'da', name: 'Danish' },
        { value: 'nl', name: 'Dutch' },
        { value: 'en', name: 'English' },
        { value: 'et', name: 'Estonian' },
        { value: 'fi', name: 'Finnish' },
        { value: 'fr', name: 'French' },
        { value: 'de', name: 'German' },
        { value: 'el', name: 'Greek' },
        { value: 'hu', name: 'Hungarian' },
        { value: 'is', name: 'Icelandic' },
        { value: 'ga', name: 'Irish' },
        { value: 'it', name: 'Italian' },
        { value: 'lv', name: 'Latvian' },
        { value: 'lt', name: 'Lithuanian' },
        { value: 'lb', name: 'Luxembourgish' },
        { value: 'mk', name: 'Macedonian' },
        { value: 'mt', name: 'Maltese' },
        { value: 'no', name: 'Norwegian' },
        { value: 'pl', name: 'Polish' },
        { value: 'pt', name: 'Portuguese' },
        { value: 'ro', name: 'Romanian' },
        { value: 'rm', name: 'Romansh' },
        { value: 'sk', name: 'Slovak' },
        { value: 'sl', name: 'Slovenian' },
        { value: 'es', name: 'Spanish' },
        { value: 'sv', name: 'Swedish' },
        { value: 'cy', name: 'Welsh' },
    ];

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const mode = ref<Mode>('list');
    const editingRuleId = ref<string | null>(null);

    // Form state
    const name = ref('');
    const selectedExternalSystemId = ref('');
    const externalSystemSearch = ref<string | null>(null);
    const matchType = ref<MatchType>('global');
    const selectedQueueIds = ref<string[]>([]);
    const selectedSkillIds = ref<string[]>([]);
    const channelMode = ref<ChannelMode>('all');
    const selectedChannelIds = ref<string[]>([]);
    const selectedChannelTypes = ref<AsrChannelType[]>([]);
    const isEnabled = ref(true);
    const audioChannelMode = ref<'' | 'mono' | 'left' | 'right'>('');
    const overviewLanguage = ref('');
    const anonymize = ref(false);
    const spellingPhrases = ref('');

    const { data: rulesData } = useQueryGetAsrConfigRules(selectedTenantId);
    const { data: filterOptionsData } = useQueryGetAsrConfigFilterOptions(selectedTenantId);
    const { data: externalSystemsData } = useQueryGetAsrConfigExternalSystems(selectedTenantId);

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const externalSystemOptions = computed<Option[]>(() =>
        (externalSystemsData.value ?? []).map((s) => ({ name: s.systemName, value: s.systemId }))
    );

    const externalSystemNameById = computed(
        () => new Map((externalSystemsData.value ?? []).map((s) => [s.systemId, s.systemName]))
    );
    const selectedExternalSystemName = computed(
        () => externalSystemNameById.value.get(selectedExternalSystemId.value) ?? ''
    );
    const queueNameById = computed(
        () => new Map((filterOptionsData.value?.queues ?? []).map((q) => [q.id, q.name]))
    );
    // Queues belong to one external system (unlike skills/channels, which aren't
    // system-scoped) - only offer queues from the system the rule is being configured for.
    const queueOptionsForSelectedSystem = computed(() =>
        (filterOptionsData.value?.queues ?? []).filter(
            (q) => q.externalSystemId === selectedExternalSystemId.value
        )
    );
    const skillNameById = computed(
        () => new Map((filterOptionsData.value?.skills ?? []).map((s) => [s.id, s.name]))
    );
    const channelNameById = computed(
        () => new Map((filterOptionsData.value?.channels ?? []).map((c) => [c.id, c.name]))
    );

    const toggleQueue = (id: string, checked: boolean) => {
        selectedQueueIds.value = checked
            ? [...selectedQueueIds.value, id]
            : selectedQueueIds.value.filter((q) => q !== id);
    };
    const toggleSkill = (id: string, checked: boolean) => {
        selectedSkillIds.value = checked
            ? [...selectedSkillIds.value, id]
            : selectedSkillIds.value.filter((s) => s !== id);
    };
    const toggleChannel = (id: string, checked: boolean) => {
        selectedChannelIds.value = checked
            ? [...selectedChannelIds.value, id]
            : selectedChannelIds.value.filter((c) => c !== id);
    };
    const toggleChannelType = (type: AsrChannelType, checked: boolean) => {
        selectedChannelTypes.value = checked
            ? [...selectedChannelTypes.value, type]
            : selectedChannelTypes.value.filter((t) => t !== type);
    };

    // Stereo/mono and spelling phrases only make sense for audio calls, never for text
    // channels (chat/email) - both are meaningless once there's no transcription to shape.
    const audioSectionApplies = computed(() => {
        if (channelMode.value === 'all') return true;
        if (channelMode.value === 'type') return selectedChannelTypes.value.includes('Audio');
        return selectedChannelIds.value.some(
            (id) => (filterOptionsData.value?.channels ?? []).find((c) => c.id === id)?.isAudio
        );
    });

    const ruleExternalSystemName = (rule: AsrConfigRule) =>
        externalSystemNameById.value.get(rule.externalSystemId) ?? rule.externalSystemId;

    const channelTypeLabel = (channelType: AsrChannelType) =>
        channelType === 'Audio'
            ? __('back-office-asr-config-channel-type-audio')
            : __('back-office-asr-config-channel-type-text');

    const matchLabel = (rule: AsrConfigRule) => {
        if (rule.queueIds.length > 0) {
            const names = rule.queueIds.map((id) => queueNameById.value.get(id) ?? id).join(', ');
            return `${__('back-office-asr-config-rule-queue')}: ${names}`;
        }
        if (rule.skillIds.length > 0) {
            const names = rule.skillIds.map((id) => skillNameById.value.get(id) ?? id).join(', ');
            return `${__('back-office-asr-config-rule-skill')}: ${names}`;
        }
        return __('back-office-asr-config-rule-all', { system: ruleExternalSystemName(rule) });
    };

    const channelLabel = (rule: AsrConfigRule) => {
        if (rule.channelIds.length > 0) {
            return rule.channelIds.map((id) => channelNameById.value.get(id) ?? id).join(', ');
        }
        if (rule.channelTypes.length > 0) {
            return rule.channelTypes.map((type) => channelTypeLabel(type)).join(', ');
        }
        return __('back-office-asr-config-all-channels', { system: ruleExternalSystemName(rule) });
    };

    const overviewLanguageName = (code: string) =>
        OVERVIEW_LANGUAGE_OPTIONS.find((lang) => lang.value === code)?.name ?? code;

    const overviewLanguageLabel = (rule: AsrConfigRule) =>
        rule.overviewLanguage
            ? overviewLanguageName(rule.overviewLanguage)
            : __('back-office-asr-config-overview-language-original');

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
    };
    const onExternalSystemSelect = (option: Option) => {
        selectedExternalSystemId.value = option.value;
    };
    const onExternalSystemClear = () => {
        selectedExternalSystemId.value = '';
    };

    watch(matchType, (type) => {
        if (type !== 'queue') selectedQueueIds.value = [];
        if (type !== 'skill') selectedSkillIds.value = [];
    });

    // Drop (rather than blindly clear) selections that no longer belong to the current
    // external system - startEdit sets selectedExternalSystemId before selectedQueueIds in
    // the same synchronous call, and this watcher only flushes once, after both are set.
    watch(selectedExternalSystemId, () => {
        const validQueueIds = new Set(queueOptionsForSelectedSystem.value.map((q) => q.id));
        selectedQueueIds.value = selectedQueueIds.value.filter((id) => validQueueIds.has(id));
    });

    watch(channelMode, (newMode) => {
        if (newMode !== 'specific') selectedChannelIds.value = [];
        if (newMode !== 'type') selectedChannelTypes.value = [];
    });

    watch(audioSectionApplies, (applies) => {
        if (!applies) {
            audioChannelMode.value = '';
            spellingPhrases.value = '';
        }
    });

    watch(selectedTenantId, () => {
        mode.value = 'list';
    });

    const clearForm = () => {
        name.value = '';
        selectedExternalSystemId.value = '';
        externalSystemSearch.value = null;
        matchType.value = 'global';
        selectedQueueIds.value = [];
        selectedSkillIds.value = [];
        channelMode.value = 'all';
        selectedChannelIds.value = [];
        selectedChannelTypes.value = [];
        isEnabled.value = true;
        audioChannelMode.value = '';
        overviewLanguage.value = '';
        anonymize.value = false;
        spellingPhrases.value = '';
    };

    const startCreate = () => {
        clearForm();
        editingRuleId.value = null;
        mode.value = 'create';
    };

    const startEdit = (rule: AsrConfigRule) => {
        clearForm();
        editingRuleId.value = rule.id;
        name.value = rule.name;
        selectedExternalSystemId.value = rule.externalSystemId;
        externalSystemSearch.value = externalSystemNameById.value.get(rule.externalSystemId) ?? '';

        if (rule.queueIds.length > 0) {
            matchType.value = 'queue';
            selectedQueueIds.value = [...rule.queueIds];
        } else if (rule.skillIds.length > 0) {
            matchType.value = 'skill';
            selectedSkillIds.value = [...rule.skillIds];
        } else {
            matchType.value = 'global';
        }

        if (rule.channelIds.length > 0) {
            channelMode.value = 'specific';
            selectedChannelIds.value = [...rule.channelIds];
        } else if (rule.channelTypes.length > 0) {
            channelMode.value = 'type';
            selectedChannelTypes.value = [...rule.channelTypes];
        } else {
            channelMode.value = 'all';
        }

        isEnabled.value = rule.isEnabled;
        audioChannelMode.value = !rule.stereo
            ? 'mono'
            : rule.customerChannel === 1
              ? 'left'
              : 'right';
        overviewLanguage.value = rule.overviewLanguage ?? '';
        anonymize.value = rule.anonymize;
        spellingPhrases.value = rule.spellingPhrases ?? '';
        mode.value = 'edit';
    };

    const cancelForm = () => {
        clearForm();
        editingRuleId.value = null;
        mode.value = 'list';
    };

    const { mutateAsync: createMutation, isPending: isCreating } = useMutationCreateAsrConfigRule();
    const { mutateAsync: updateMutation, isPending: isUpdating } = useMutationUpdateAsrConfigRule();
    const { mutateAsync: deleteMutation } = useMutationDeleteAsrConfigRule();

    const isPending = computed(() => isCreating.value || isUpdating.value);
    const deletingRuleId = ref<string | null>(null);

    const deleteRule = async (ruleId: string) => {
        const confirmed = await confirmationDialogStore.openDialog({
            title: __('delete'),
            message: __('back-office-asr-config-delete-confirm'),
        });
        if (!confirmed) return;

        deletingRuleId.value = ruleId;
        try {
            await deleteMutation({ tenantId: selectedTenantId.value, id: ruleId });
            cancelForm();
        } finally {
            deletingRuleId.value = null;
        }
    };

    const canSubmit = computed(() => {
        if (!selectedTenantId.value || !selectedExternalSystemId.value) return false;
        if (matchType.value === 'queue' && selectedQueueIds.value.length === 0) return false;
        if (matchType.value === 'skill' && selectedSkillIds.value.length === 0) return false;
        if (channelMode.value === 'specific' && selectedChannelIds.value.length === 0) return false;
        if (channelMode.value === 'type' && selectedChannelTypes.value.length === 0) return false;
        if (audioSectionApplies.value && !audioChannelMode.value) return false;
        return true;
    });

    const submit = async () => {
        if (!canSubmit.value) return;

        const data = {
            name: name.value || null,
            externalSystemId: selectedExternalSystemId.value,
            queueIds: matchType.value === 'queue' ? selectedQueueIds.value : [],
            skillIds: matchType.value === 'skill' ? selectedSkillIds.value : [],
            channelIds: channelMode.value === 'specific' ? selectedChannelIds.value : [],
            channelTypes: channelMode.value === 'type' ? selectedChannelTypes.value : [],
            isEnabled: isEnabled.value,
            stereo: audioChannelMode.value === 'left' || audioChannelMode.value === 'right',
            customerChannel:
                audioChannelMode.value === 'left'
                    ? 1
                    : audioChannelMode.value === 'right'
                      ? 2
                      : null,
            overviewLanguage: overviewLanguage.value || null,
            anonymize: anonymize.value,
            spellingPhrases: spellingPhrases.value || null,
        };

        if (mode.value === 'edit' && editingRuleId.value) {
            await updateMutation({
                tenantId: selectedTenantId.value,
                id: editingRuleId.value,
                data,
            });
        } else {
            await createMutation({ tenantId: selectedTenantId.value, data });
        }

        successDialogStore.openDialog({ message: __('back-office-asr-config-success') });
        cancelForm();
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-asr-config') }}</h1>
        </template>
        <template #content>
            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="asr-config-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onTenantSelect"
                    @clear="onTenantClear"
                />

                <template v-if="selectedTenantId">
                    <template v-if="mode === 'list'">
                        <div>
                            <EchoButton type="button" variant="primary" @click="startCreate">
                                {{ __('back-office-asr-config-add') }}
                            </EchoButton>
                        </div>
                        <div class="flex flex-col gap-3">
                            <div
                                v-for="rule in rulesData ?? []"
                                :key="rule.id"
                                class="flex items-center justify-between rounded-md border border-gray-light px-4 py-3"
                            >
                                <div>
                                    <p class="text-body-bold">
                                        {{ rule.name }}
                                        <span v-if="!rule.isEnabled" class="text-gray-medium">
                                            ({{ __('back-office-asr-config-disabled') }})
                                        </span>
                                    </p>
                                    <p class="text-body text-gray-medium">
                                        {{ matchLabel(rule) }} · {{ channelLabel(rule) }}
                                    </p>
                                    <p class="text-body text-gray-medium">
                                        {{ __('back-office-asr-config-overview-language') }}:
                                        {{ overviewLanguageLabel(rule) }}
                                        <template v-if="rule.stereo">
                                            · {{ __('back-office-asr-config-stereo') }} ({{
                                                rule.customerChannel === 1
                                                    ? __(
                                                          'back-office-asr-config-customer-channel-left'
                                                      )
                                                    : __(
                                                          'back-office-asr-config-customer-channel-right'
                                                      )
                                            }})
                                        </template>
                                        <template v-if="rule.anonymize">
                                            · {{ __('back-office-asr-config-anonymize') }}
                                        </template>
                                    </p>
                                </div>
                                <div class="flex gap-2">
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="startEdit(rule)"
                                    >
                                        {{ __('edit') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        :loading="deletingRuleId === rule.id"
                                        @click="deleteRule(rule.id)"
                                    >
                                        {{ __('delete') }}
                                    </EchoButton>
                                </div>
                            </div>
                            <p v-if="rulesData?.length === 0" class="text-body text-gray-medium">
                                {{ __('back-office-asr-config-none') }}
                            </p>
                        </div>
                    </template>

                    <template v-else>
                        <form class="flex flex-col gap-6" @submit.prevent="submit">
                            <FormGroup>
                                <div class="flex flex-col gap-1">
                                    <label for="asr-config-name" class="text-overline">
                                        {{ __('back-office-asr-config-name') }}
                                    </label>
                                    <TextInput id="asr-config-name" v-model="name" />
                                </div>

                                <AutoComplete
                                    v-model="externalSystemSearch"
                                    input-id="asr-config-external-system"
                                    :label="__('back-office-asr-config-external-system')"
                                    :options="externalSystemOptions"
                                    :disabled="mode === 'edit'"
                                    @select="onExternalSystemSelect"
                                    @clear="onExternalSystemClear"
                                />

                                <fieldset
                                    class="flex flex-col gap-5 rounded-md border border-gray-light p-4"
                                >
                                    <legend class="text-overline mb-1 block">
                                        {{ __('back-office-asr-config-match-type') }}
                                    </legend>

                                    <div class="flex flex-col gap-2">
                                        <p class="text-body-bold">
                                            {{ __('back-office-asr-config-queues-skills-heading') }}
                                        </p>

                                        <SelectInput id="asr-config-match-type" v-model="matchType">
                                            <option value="global">
                                                {{
                                                    __('back-office-asr-config-rule-all', {
                                                        system: selectedExternalSystemName,
                                                    })
                                                }}
                                            </option>
                                            <option value="queue">
                                                {{ __('back-office-asr-config-option-queue') }}
                                            </option>
                                            <option value="skill">
                                                {{ __('back-office-asr-config-option-skill') }}
                                            </option>
                                        </SelectInput>

                                        <Accordion v-if="matchType === 'queue'">
                                            <template #summary>
                                                <span class="flex-1">
                                                    <span class="text-overline">
                                                        {{
                                                            __('back-office-asr-config-rule-queue')
                                                        }}
                                                    </span>
                                                    <span class="ml-3 text-body text-gray-medium">
                                                        <template
                                                            v-if="selectedQueueIds.length === 0"
                                                        >
                                                            {{
                                                                __(
                                                                    'back-office-asr-config-none-selected'
                                                                )
                                                            }}
                                                        </template>
                                                        <template v-else>
                                                            ({{ selectedQueueIds.length }})
                                                        </template>
                                                    </span>
                                                </span>
                                            </template>
                                            <div class="flex flex-col gap-1 px-5 pb-3">
                                                <CheckboxInput
                                                    v-for="queue in queueOptionsForSelectedSystem"
                                                    :id="`asr-config-queue-${queue.id}`"
                                                    :key="queue.id"
                                                    :model-value="
                                                        selectedQueueIds.includes(queue.id)
                                                    "
                                                    @update:model-value="
                                                        toggleQueue(queue.id, $event ?? false)
                                                    "
                                                >
                                                    {{ queue.name }}
                                                </CheckboxInput>
                                            </div>
                                        </Accordion>

                                        <Accordion v-if="matchType === 'skill'">
                                            <template #summary>
                                                <span class="flex-1">
                                                    <span class="text-overline">
                                                        {{
                                                            __('back-office-asr-config-rule-skill')
                                                        }}
                                                    </span>
                                                    <span class="ml-3 text-body text-gray-medium">
                                                        <template
                                                            v-if="selectedSkillIds.length === 0"
                                                        >
                                                            {{
                                                                __(
                                                                    'back-office-asr-config-none-selected'
                                                                )
                                                            }}
                                                        </template>
                                                        <template v-else>
                                                            ({{ selectedSkillIds.length }})
                                                        </template>
                                                    </span>
                                                </span>
                                            </template>
                                            <div class="flex flex-col gap-1 px-5 pb-3">
                                                <CheckboxInput
                                                    v-for="skill in filterOptionsData?.skills ?? []"
                                                    :id="`asr-config-skill-${skill.id}`"
                                                    :key="skill.id"
                                                    :model-value="
                                                        selectedSkillIds.includes(skill.id)
                                                    "
                                                    @update:model-value="
                                                        toggleSkill(skill.id, $event ?? false)
                                                    "
                                                >
                                                    {{ skill.name }}
                                                </CheckboxInput>
                                            </div>
                                        </Accordion>
                                    </div>

                                    <div class="flex flex-col gap-2">
                                        <p class="text-body-bold">
                                            {{ __('back-office-asr-config-channels-heading') }}
                                        </p>

                                        <SelectInput
                                            id="asr-config-channel-mode"
                                            v-model="channelMode"
                                        >
                                            <option value="all">
                                                {{
                                                    __('back-office-asr-config-all-channels', {
                                                        system: selectedExternalSystemName,
                                                    })
                                                }}
                                            </option>
                                            <option value="type">
                                                {{
                                                    __('back-office-asr-config-option-channel-type')
                                                }}
                                            </option>
                                            <option value="specific">
                                                {{ __('back-office-asr-config-option-channel') }}
                                            </option>
                                        </SelectInput>

                                        <Accordion v-if="channelMode === 'type'">
                                            <template #summary>
                                                <span class="flex-1">
                                                    <span class="text-overline">
                                                        {{
                                                            __(
                                                                'back-office-asr-config-option-channel-type'
                                                            )
                                                        }}
                                                    </span>
                                                    <span class="ml-3 text-body text-gray-medium">
                                                        <template
                                                            v-if="selectedChannelTypes.length === 0"
                                                        >
                                                            {{
                                                                __(
                                                                    'back-office-asr-config-none-selected'
                                                                )
                                                            }}
                                                        </template>
                                                        <template v-else>
                                                            ({{ selectedChannelTypes.length }})
                                                        </template>
                                                    </span>
                                                </span>
                                            </template>
                                            <div class="flex flex-col gap-1 px-5 pb-3">
                                                <CheckboxInput
                                                    id="asr-config-channel-type-audio"
                                                    :model-value="
                                                        selectedChannelTypes.includes('Audio')
                                                    "
                                                    @update:model-value="
                                                        toggleChannelType('Audio', $event ?? false)
                                                    "
                                                >
                                                    {{
                                                        __(
                                                            'back-office-asr-config-channel-type-audio'
                                                        )
                                                    }}
                                                </CheckboxInput>
                                                <CheckboxInput
                                                    id="asr-config-channel-type-text"
                                                    :model-value="
                                                        selectedChannelTypes.includes('Text')
                                                    "
                                                    @update:model-value="
                                                        toggleChannelType('Text', $event ?? false)
                                                    "
                                                >
                                                    {{
                                                        __(
                                                            'back-office-asr-config-channel-type-text'
                                                        )
                                                    }}
                                                </CheckboxInput>
                                            </div>
                                        </Accordion>

                                        <Accordion v-if="channelMode === 'specific'">
                                            <template #summary>
                                                <span class="flex-1">
                                                    <span class="text-overline">
                                                        {{
                                                            __(
                                                                'back-office-asr-config-rule-channel'
                                                            )
                                                        }}
                                                    </span>
                                                    <span class="ml-3 text-body text-gray-medium">
                                                        <template
                                                            v-if="selectedChannelIds.length === 0"
                                                        >
                                                            {{
                                                                __(
                                                                    'back-office-asr-config-none-selected'
                                                                )
                                                            }}
                                                        </template>
                                                        <template v-else>
                                                            ({{ selectedChannelIds.length }})
                                                        </template>
                                                    </span>
                                                </span>
                                            </template>
                                            <div class="flex flex-col gap-1 px-5 pb-3">
                                                <CheckboxInput
                                                    v-for="channel in filterOptionsData?.channels ??
                                                    []"
                                                    :id="`asr-config-channel-${channel.id}`"
                                                    :key="channel.id"
                                                    :model-value="
                                                        selectedChannelIds.includes(channel.id)
                                                    "
                                                    @update:model-value="
                                                        toggleChannel(channel.id, $event ?? false)
                                                    "
                                                >
                                                    {{ channel.name }}
                                                </CheckboxInput>
                                            </div>
                                        </Accordion>
                                    </div>
                                </fieldset>

                                <fieldset
                                    v-if="audioSectionApplies"
                                    class="flex flex-col gap-4 rounded-md border border-gray-light p-4"
                                >
                                    <legend class="text-overline mb-1 block">
                                        {{ __('back-office-asr-config-audio-settings-heading') }}
                                    </legend>

                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="asr-config-audio-channel-mode"
                                            class="text-overline"
                                        >
                                            {{ __('back-office-asr-config-audio-channels') }}
                                        </label>
                                        <SelectInput
                                            id="asr-config-audio-channel-mode"
                                            v-model="audioChannelMode"
                                        >
                                            <option value="" disabled>–</option>
                                            <option value="mono">
                                                {{ __('back-office-asr-config-audio-mono') }}
                                            </option>
                                            <option value="left">
                                                {{
                                                    __(
                                                        'back-office-asr-config-customer-channel-left'
                                                    )
                                                }}
                                            </option>
                                            <option value="right">
                                                {{
                                                    __(
                                                        'back-office-asr-config-customer-channel-right'
                                                    )
                                                }}
                                            </option>
                                        </SelectInput>
                                    </div>

                                    <div class="flex flex-col gap-1">
                                        <label
                                            for="asr-config-spelling-phrases"
                                            class="text-overline"
                                        >
                                            {{ __('back-office-asr-config-spelling-phrases') }}
                                        </label>
                                        <TextareaInput
                                            id="asr-config-spelling-phrases"
                                            v-model="spellingPhrases"
                                        />
                                    </div>
                                </fieldset>

                                <div class="flex flex-col gap-1">
                                    <label for="asr-config-overview-language" class="text-overline">
                                        {{ __('back-office-asr-config-overview-language') }}
                                    </label>
                                    <SelectInput
                                        id="asr-config-overview-language"
                                        v-model="overviewLanguage"
                                    >
                                        <option value="">
                                            {{
                                                __(
                                                    'back-office-asr-config-overview-language-original'
                                                )
                                            }}
                                        </option>
                                        <option
                                            v-for="lang in OVERVIEW_LANGUAGE_OPTIONS"
                                            :key="lang.value"
                                            :value="lang.value"
                                        >
                                            {{ lang.name }}
                                        </option>
                                    </SelectInput>
                                </div>

                                <fieldset
                                    class="flex flex-col gap-2 rounded-md border border-gray-light p-4"
                                >
                                    <legend class="text-overline mb-1 block">
                                        {{ __('back-office-asr-config-flags-heading') }}
                                    </legend>

                                    <CheckboxInput id="asr-config-anonymize" v-model="anonymize">
                                        {{ __('back-office-asr-config-anonymize') }}
                                    </CheckboxInput>
                                    <p class="text-body text-gray-medium">
                                        {{ __('back-office-asr-config-anonymize-help') }}
                                    </p>

                                    <CheckboxInput id="asr-config-is-enabled" v-model="isEnabled">
                                        {{ __('back-office-asr-config-is-enabled') }}
                                    </CheckboxInput>
                                </fieldset>
                            </FormGroup>

                            <div class="flex justify-between">
                                <EchoButton
                                    v-if="mode === 'edit' && editingRuleId"
                                    type="button"
                                    variant="secondary"
                                    :loading="deletingRuleId === editingRuleId"
                                    @click="deleteRule(editingRuleId)"
                                >
                                    {{ __('delete') }}
                                </EchoButton>
                                <div class="flex gap-3">
                                    <EchoButton
                                        type="submit"
                                        variant="primary"
                                        :disabled="!canSubmit"
                                        :loading="isPending"
                                    >
                                        {{ __('back-office-asr-config-save') }}
                                    </EchoButton>
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="cancelForm"
                                    >
                                        {{ __('cancel') }}
                                    </EchoButton>
                                </div>
                            </div>
                        </form>
                    </template>
                </template>
            </div>
        </template>
    </SettingsPageLayout>
</template>
